import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BodyText, Card, ElementContainer, SideDrawer, TextField, withStyles } from 'vopay';
import { Button, Checkbox, Skeleton, UtilButton } from 'UI';
import { usePermissions } from '@/providers/Permissions.provider';
import { useClientLogin } from '@/providers/ClientLogin.provider';
import useFetchRolePermissionsCatalog from '@/hooks/useApi/useFetchRolePermissionsCatalog';
import useCreateUserRole from '@/hooks/useApi/useCreateUserRole';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import { useToast } from '@/providers/Toast.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import AccountUserPermissionsForm, {
  AccountUserPermissionsFormRef,
  PermissionFormValues,
} from '../UserPermissions/AccountUserPermissionsForm';
import PermissionsSearchField from '../UserPermissions/PermissionsSearchField';
import styles from './CreateRoleDrawer.module.scss';

const cn = withStyles(styles);

const CREATE_ROLE_FORM_ID = 'createRolePermissionsForm';

type CreateRoleDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onRoleCreated: () => void;
};

type Step = 'name' | 'permissions';

const CreateRoleDrawer: FC<CreateRoleDrawerProps> = ({ isOpen, onClose, onRoleCreated }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UserRoles.createDrawer' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });
  const { permissions } = usePermissions();

  const [step, setStep] = useState<Step>('name');
  const [roleName, setRoleName] = useState('');
  const [isApplyingToAllSubaccounts, setIsApplyingToAllSubaccounts] = useState(false);
  const [hasAtLeastOnePermission, setHasAtLeastOnePermission] = useState(false);
  const [permissionSearchTerm, setPermissionSearchTerm] = useState('');
  const [isAllSelected, setIsAllSelected] = useState(false);

  const { response: loginResponse } = useClientLogin();
  const loggedInEmail = loginResponse?.LoginUserName;

  const { catalog, fetching: fetchingCatalog, fetchCatalog } = useFetchRolePermissionsCatalog();
  const { submitting, createUserRole } = useCreateUserRole();
  const [, , , fetchSubAccountsAsync] = useFetchSubAccounts({});
  const formRef = useRef<AccountUserPermissionsFormRef | null>(null);
  const toast = useToast();
  const alertDialog = useAlertDialog();

  // Sub-accounts checkbox is only shown when the account has sub-accounts enabled.
  // We use ViewSubaccounts as a proxy for "this account has the feature".
  const hasSubaccounts = !!permissions.ViewSubaccounts;

  // Reset state every time the drawer reopens.
  useEffect(() => {
    if (isOpen) {
      setStep('name');
      setRoleName('');
      setIsApplyingToAllSubaccounts(false);
      setHasAtLeastOnePermission(false);
      setPermissionSearchTerm('');
    }
  }, [isOpen]);

  // Fetch the permissions catalog the first time we enter the permissions step.
  // Deps are intentionally limited to step/loggedInEmail. fetchCatalog (and the
  // catalog it populates) must NOT be deps: the hook re-creates fetchCatalog on
  // every render (Direct Pattern, no useCallback), so listing it re-fired the
  // fetch on each render. The catalog-empty guard prevents a redundant re-fetch.
  useEffect(() => {
    if (step === 'permissions' && loggedInEmail && Object.keys(catalog).length === 0) {
      fetchCatalog(loggedInEmail).catch(() => {
        toast.neutral(t('catalogLoadError', { defaultValue: 'Unable to load permissions.' }));
      });
    }
  }, [step, loggedInEmail]);

  const handleContinue = () => {
    setStep('permissions');
  };


  const handleCreate = async (values: PermissionFormValues) => {
    const selectedPermissionKeys = Object.entries(values)
      .filter(([, isSelected]) => isSelected)
      .map(([permissionKey]) => permissionKey);

    const rolePayload = {
      Name: roleName.trim(),
      Permissions: selectedPermissionKeys.join(','),
      ApplyToAllSubaccounts: isApplyingToAllSubaccounts,
    };

    try {
      await createUserRole(rolePayload);

      if (isApplyingToAllSubaccounts) {
        const subAccountsRes = await fetchSubAccountsAsync({ ShowToken: true, ActiveOnly: true });
        if (!subAccountsRes.Success) {
          throw new Error(subAccountsRes.ErrorMessage || 'Failed to fetch sub-accounts');
        }
        const subAccounts = subAccountsRes.Subaccounts;

        const results = await Promise.all(
          subAccounts.map(async (subAccount) => {
            try {
              await createUserRole(rolePayload, subAccount.Token);
              return { ok: true };
            } catch (error) {
              console.error(
                `Failed to create role "${rolePayload.Name}" in sub-account ${subAccount.AccountName}:`,
                error,
              );
              return { ok: false };
            }
          }),
        );

        const numCreated = results.filter((r) => r.ok).length;
        const numFailed = results.filter((r) => !r.ok).length;

        if (numFailed > 0) {
          toast.neutral(
            t('partialSubaccountPropagation', {
              defaultValue: `Role "${roleName}" created. Applied to ${numCreated} of ${subAccounts.length} sub-accounts (${numFailed} failed).`,
            }),
          );
        } else {
          toast.success(
            t('subaccountPropagationSuccess', {
              defaultValue: `Role "${roleName}" created and applied to ${subAccounts.length} sub-account(s).`,
            }),
          );
        }
      } else {
        toast.success(
          t('successMessage', { defaultValue: `Role "${roleName}" was created successfully.` }),
        );
      }

      onRoleCreated();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      alertDialog.error(t('errorTitle', { defaultValue: 'Could not create role' }), message);
    }
  };

  const handleToggleAll = () => {
    formRef.current?.toggleAll(!isAllSelected);
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Box flx="col" gapSize="12px">
          <span>
            {step === 'name'
              ? t('step1.title', { defaultValue: 'Create Role' })
              : t('step2.title', { defaultValue: 'Role Permissions' })}
          </span>
          {step === 'permissions' && (
            <>
              <BodyText weight="md">
                {t('step2.subtitle', {
                  defaultValue: `Configure the permissions for ${roleName} role.`,
                })}
              </BodyText>
              <div className={cn('headerSearchRow')}>
                <PermissionsSearchField
                  name="rolePermissionSearch"
                  value={permissionSearchTerm}
                  onChange={setPermissionSearchTerm}
                  placeholder={t('step2.searchPlaceholder', { defaultValue: 'Search Permissions' })}
                />
                <UtilButton
                  size="medium"
                  onClick={handleToggleAll}
                  className={cn('toggleAllButton')}
                >
                  {isAllSelected
                    ? t('step2.disableAll', { defaultValue: 'Disable All' })
                    : t('step2.enableAll', { defaultValue: 'Enable All' })}
                </UtilButton>
              </div>
            </>
          )}
        </Box>
      }
      footer={
        <Box className={cn('footer')}>
          {step === 'name' ? (
            <Button colorScheme="primary" disabled={!roleName.trim()} onClick={handleContinue}>
              {tGlobal('continue')}
            </Button>
          ) : (
            <>
              <Button colorScheme="secondary" onClick={() => setStep('name')}>
                {tGlobal('back')}
              </Button>
              <Button
                colorScheme="primary"
                disabled={!hasAtLeastOnePermission || fetchingCatalog || submitting}
                form={CREATE_ROLE_FORM_ID}
                type="submit"
              >
                {t('step2.create', { defaultValue: 'Create Role' })}
              </Button>
            </>
          )}
        </Box>
      }
    >
      {step === 'name' && (
        <div className={cn('body')}>
          <Card hoverEffect={false}>
            <ElementContainer
              label={t('step1.roleName', { defaultValue: 'Role name' })}
              required
              htmlFor="roleName"
              input={
                <TextField
                  name="roleName"
                  placeholder={t('step1.roleNamePlaceholder', { defaultValue: 'e.g. Admin' })}
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value.trim())}
                  variant="outlineShadow"
                />
              }
            />

            {hasSubaccounts && (
              <Checkbox
                name="applyToAllSubaccounts"
                checked={isApplyingToAllSubaccounts}
                onChange={setIsApplyingToAllSubaccounts}
                label={t('step1.applyToAllSubaccounts', {
                  defaultValue: 'Create this role for all existing and future Sub-Accounts',
                })}
              />
            )}
          </Card>
        </div>
      )}

      {step === 'permissions' && (
        <div className={cn('body')}>
          {fetchingCatalog && (
            <>
              <Skeleton height="60px" />
              <Skeleton height="60px" />
              <Skeleton height="60px" />
            </>
          )}
          {!fetchingCatalog && Object.keys(catalog).length > 0 && (
            <AccountUserPermissionsForm
              ref={formRef}
              permissions={catalog}
              id={CREATE_ROLE_FORM_ID}
              searchTerm={permissionSearchTerm}
              onSubmit={handleCreate}
              onFormStateChange={(_, isValid) => setHasAtLeastOnePermission(isValid)}
              onAllEnabledChange={setIsAllSelected}
            />
          )}
        </div>
      )}
    </SideDrawer>
  );
};

export default CreateRoleDrawer;
